import { JetsSeatMap } from "@chqfly/jets-seatmap-react-lib-pub";
import classNames from "classnames";
import dayjs from "dayjs";

import { useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import seatmap_guide from "@/assets/seatmap.png";
import usePlatform from "@/hooks/usePlatform";
import { FlightItem } from "@/typings";
import request from "@/utils/request";

import { CLASS_CODE_MAP, INIT_DATA } from "./constants";

const Loading = () => {
  return (
    <div className="flex justify-center items-center h-[300px]">
      <div className="text-center">
        <CircularProgress />
        <div>Loading...</div>
      </div>
    </div>
  );
};

function SitemapDialog(props: { data: FlightItem; open: boolean; onClose: () => void }) {
  const { open, onClose } = props;

  const { isPc } = usePlatform();

  const [availabilityData, setAvailabilityData] = useState([]);

  const [flightNumber, setFlightNumber] = useState(() => {
    return props.data.fare.products[0].flight_number;
  });

  const data = useMemo(() => {
    const flightItem = props.data.fare.products.find((item) => item.flight_number === flightNumber);

    if (!flightItem) {
      return { ...INIT_DATA, flight: null };
    }

    const flightData = {
      id: flightItem?.flight_number,
      airlineCode: flightItem?.airline_code,
      flightNo: flightItem?.flight_number.replace(flightItem?.airline_code, ""),
      departureDate: dayjs(flightItem?.departure_time).format("YYYY-MM-DD"),
      departure: flightItem?.origin,
      arrival: flightItem?.destination,
      cabinClass:
        CLASS_CODE_MAP[
          flightItem?.cabin_type as keyof typeof CLASS_CODE_MAP
        ]?.toLocaleUpperCase?.(),
    };

    console.log("[ flightData ] >", flightData);
    return { ...INIT_DATA, flight: flightData };
  }, [props.data, flightNumber]);

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onClick={(e) => e.stopPropagation()}
      sx={{
        ...(isPc
          ? {
              ".MuiPaper-root": {
                minHeight: "70%",
              },
            }
          : {
              ".MuiPaper-root": {
                width: "100%",
                "max-height": "100%",
                margin: 0,
                height: "100%",
              },
            }),
      }}
    >
      <DialogTitle id="alert-dialog-title" className="flex justify-between items-center relative">
        <ToggleButtonGroup
          color="primary"
          value={flightNumber}
          exclusive
          onChange={(_, value) => {
            if (value) {
              setFlightNumber(value);
            }
          }}
          aria-label="Platform"
          size="small"
          className={isPc ? "w-full mx-[100px]" : "w-full"}
        >
          {props.data.fare.products.map((item, index) => {
            return (
              <ToggleButton
                key={item.hash}
                value={item.flight_number}
                sx={{
                  ...(index === 0 && {
                    borderTopLeftRadius: "30px",
                    borderBottomLeftRadius: "30px",
                  }),
                  ...(index === props.data.fare.products.length - 1 && {
                    borderTopRightRadius: "30px",
                    borderBottomRightRadius: "30px",
                  }),
                  flex: 1,
                }}
              >
                <div>
                  <Typography className="!text-[14px]">{`${item.origin}-${item.destination}`}</Typography>
                  <Typography className="!text-[12px]">{`${item.flight_number} - ${item.aircraft}`}</Typography>
                </div>
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>

        <div
          className={classNames(
            "absolute",
            isPc ? "top-[50%] translate-y-[-50%] right-[20px]" : "right-[-5px] top-[-5px]"
          )}
        >
          <IconButton aria-label="delete" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent
        className="flex justify-center p-0"
        sx={{
          ".deck-exit__image": {
            width: "100%",
          },
        }}
      >
        {data.flight && (
          <JetsSeatMap
            config={{
              apiUrl: "https://www.awardtool-api.com",
              apiAppId: "demo",
              apiKey: "demo",
              ...data.config,
              request: request,
            }}
            availability={availabilityData}
            flight={data.flight}
            componentOverrides={{
              JetsNotInit: Loading,
            }}
            key={data.flight.id}
            onSeatMapInited={(d: any) => {
              if (d?.availabilityData?.availableSeats) {
                setAvailabilityData(d.availabilityData.availableSeats);
              }
            }}
          />
        )}

        <div
          className="absolute bottom-[10px] right-[10px] w-[160px] p-[4px] bg-white"
          style={{
            border: "1px solid rgba(0, 0, 0, 0.12)",
          }}
        >
          <div className="mb-[10px] text-black px-[9px]">Seat Map Key</div>
          <img className="size-full" src={seatmap_guide} alt="" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SitemapDialog;
